import { useCallback, useEffect, useState } from 'react';
import { formatMinor, type CategoryDto, type TransactionDto } from '@trestly/shared';
import { useDeleteTransaction, useUpdateTransaction } from '../api/hooks';
import { AmountInput, tryParseAmount } from './AmountInput';
import { CategoryChips } from './CategoryChips';
import { CurrencyToggle } from './CurrencyToggle';
import { DateChips } from './DateChips';
import { Field, FieldGroup } from './Field';
import { Sheet } from './Sheet';
import { Spinner } from './Spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { confirmAction, haptic } from '@/telegram';
import { t } from '../i18n';

interface TransactionSheetProps {
  /** false — лист доигрывает анимацию закрытия; трата при этом остаётся прежней. */
  open?: boolean;
  transaction: TransactionDto;
  /** Справочник категорий: у самой траты есть только categoryId. */
  categories: CategoryDto[];
  onClose: () => void;
}

/**
 * Правка траты. Один и тот же лист открывается из «Трат» и из карточки
 * категории в «Бюджете» — раньше правка была доступна только из бюджета,
 * и найденную в списке трату приходилось искать там же.
 */
export function TransactionSheet({
  open = true,
  transaction,
  categories,
  onClose,
}: TransactionSheetProps): JSX.Element {
  const updateTransaction = useUpdateTransaction();
  const deleteTransaction = useDeleteTransaction();

  const [amount, setAmount] = useState(() => formatMinor(transaction.amountMinor, transaction.currency));
  const [currency, setCurrency] = useState(transaction.currency);
  const [categoryId, setCategoryId] = useState<number | null>(transaction.categoryId);
  const [spentOn, setSpentOn] = useState(transaction.spentOn);
  const [note, setNote] = useState(transaction.note ?? '');
  const [submitted, setSubmitted] = useState(false);

  // Лист переиспользуется под разные траты: при смене id сбрасываем поля.
  useEffect(() => {
    setAmount(formatMinor(transaction.amountMinor, transaction.currency));
    setCurrency(transaction.currency);
    setCategoryId(transaction.categoryId);
    setSpentOn(transaction.spentOn);
    setNote(transaction.note ?? '');
    setSubmitted(false);
    updateTransaction.reset();
    deleteTransaction.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction.id]);

  const busy = updateTransaction.isPending || deleteTransaction.isPending;

  const handleSave = useCallback((): void => {
    setSubmitted(true);
    const amountMinor = tryParseAmount(amount, currency);
    if (amountMinor === undefined) {
      haptic.result('error');
      return;
    }
    updateTransaction.mutate(
      {
        id: transaction.id,
        patch: {
          amountMinor,
          currency,
          categoryId,
          spentOn,
          note: note.trim() === '' ? null : note.trim(),
        },
      },
      {
        onSuccess: () => {
          haptic.result('success');
          onClose();
        },
        onError: () => haptic.result('error'),
      },
    );
    // мутация нестабильна между рендерами — сознательно вне зависимостей
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency, categoryId, spentOn, note, transaction.id, onClose]);

  const handleDelete = async (): Promise<void> => {
    const ok = await confirmAction(t('components.tx.deleteConfirm'));
    if (!ok) return;
    deleteTransaction.mutate(transaction.id, {
      onSuccess: () => {
        haptic.result('success');
        onClose();
      },
      onError: () => haptic.result('error'),
    });
  };

  const error = updateTransaction.error?.message ?? deleteTransaction.error?.message ?? null;

  return (
    <Sheet
      open={open}
      title={t('components.tx.title')}
      description={
        transaction.source === 'payment' ? t('components.tx.fromPayment') : undefined
      }
      onClose={onClose}
    >
      <AmountInput
        value={amount}
        onChange={setAmount}
        currency={currency}
        showError={submitted}
        autoFocus
        label={t('components.tx.amount')}
        trailing={
          <CurrencyToggle
            value={currency}
            onChange={setCurrency}
            size="compact"
            className="h-15"
            disabled={busy}
          />
        }
      />

      <FieldGroup label={t('components.category')}>
        <CategoryChips categories={categories} value={categoryId} onChange={setCategoryId} />
      </FieldGroup>

      <FieldGroup label={t('components.date')} hint={t('components.tx.dateHint')}>
        <DateChips value={spentOn} onChange={setSpentOn} />
      </FieldGroup>

      <Field label={t('components.note')}>
        <Input
          type="text"
          placeholder={t('components.optional')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>

      {error ? <div className="mb-3 text-[13px] text-destructive">{error}</div> : null}

      <Button className="w-full" disabled={busy} onClick={handleSave}>
        {busy && updateTransaction.isPending ? <Spinner small /> : t('components.save')}
      </Button>

      <Button
        variant="destructive"
        className="mt-2 w-full"
        disabled={busy}
        onClick={() => void handleDelete()}
      >
        {t('components.tx.delete')}
      </Button>
    </Sheet>
  );
}
